import { useState, useEffect, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import ConnectButton from '../components/connect-button';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import A from '@mui/material/Link';
import NFTVideo from '../components/nft2026';
import { T, P } from '../components/Typography';
import theme from '../theme';
import api from '../api';
import { VFlex, HFlex } from '../components/layout';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { countdown } from '../state/index';
import { fetchDaddyTweet } from '../state/dropClient.js';
import { saveLastTx, loadLastTx } from '../state/lastTx.js';
import { lookupGroupId } from '../wallet/dispenser.js';
import { NFT2026ID } from '../constants.js';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const D = () => <Divider sx={{ m: 4, mb: 4 }} />;

const Loading = () => <Box sx={{width: 100}}><LinearProgress /></Box>;

function NFT({ account, discountToken, NFT }) {

  const location = useLocation();
  const navigate = useNavigate();

  // txId is shown on the page; groupId (base64) powers the Allo group link.
  const [txId, setTxId] = useState();
  const [groupId, setGroupId] = useState();
  const [showTxId, setShowTxId] = useState();
  // The URL the share-tweet quotes, served by cf-backend. '' => omit the quote.
  const [daddyTweet, setDaddyTweet] = useState('');

  useEffect(() => setShowTxId(txId ?? <Loading />), [txId]);

  useEffect(() => {
    fetchDaddyTweet().then(setDaddyTweet);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const raw = decodeURIComponent(location.hash.slice(1));
      let record = null;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') record = parsed;
      } catch {
        // Legacy links carried a single bare id in the hash.
        if (/^[0-9A-Z]{52}$/.test(raw)) record = { txId: raw };
        else if (/^[A-Za-z0-9+\/=]{32,58}$/.test(raw)) record = { groupId: raw };
      }
      if (record) {
        setTxId(record.txId);
        setGroupId(record.groupId);
        saveLastTx(2026, record);
      }
    } else {
      // Nothing in the URL (reload / direct visit) — recover the last successful one.
      const stored = loadLastTx(2026);
      if (stored) {
        setTxId(stored.txId);
        setGroupId(stored.groupId);
      }
    }
  }, [location]);

  // Only the txn id arrived (shared/legacy link) — resolve the group id from the
  // indexer so the Allo group link works, and persist it for reloads.
  useEffect(() => {
    if (!txId || groupId) return;
    let stale = false;
    lookupGroupId(txId).then((gid) => {
      if (stale || !gid) return;
      setGroupId(gid);
      saveLastTx(2026, { txId, groupId: gid });
    });
    return () => { stale = true; };
  }, [txId, groupId]);

  const viewInExplorer = useCallback(() => {
    // groupId is base64 — percent-encode for the URL path.
    const url = groupId ? `https://allo.info/tx/group/${encodeURIComponent(groupId)}` : `https://allo.info/tx/${txId}`;
    if (!window.open(url))
      location.href = url;
  }, [groupId, txId]);

  const viewInNFTExplorer = useCallback(() => {
    const url = `https://explorer.perawallet.app/asset/${NFT2026ID}`;
    if (!window.open(url))
      location.href = url;
  }, [NFT2026ID]);

  const tweet = useCallback(() => {
    const body = `Happy Birthday, #Algorand MainNet!

🏇 7 years of non-stop Algorand since June 12, 2019

I got @d13_co's animated #AlgorandBirthday #NFT`;
    // Only quote a tweet when the worker provided a URL; otherwise omit it.
    const text = daddyTweet ? `${body}\n\n${daddyTweet}` : body;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    if (!window.open(url))
      location.href = url;
  }, [daddyTweet]);

  return <Box>
    <Typography align="center" variant="h1" color="primary" sx={{ mb: 3 }}>Enjoy your NFT!</Typography>
    <Box sx={{ display: 'flex', mb: 2, flexDirection: 'row', justifyContent: 'center' }}>
      <NFTVideo />
    </Box>
    <HFlex sx={{ mt: 2 }}><Box>Success!</Box></HFlex>
    <HFlex sx={{ mt: 2 }}><Box>Transaction ID:</Box></HFlex>
    <HFlex><Typography>{showTxId}</Typography></HFlex>
    <D />
    <HFlex sx={{ mt: 0 }}><Button variant="outlined" disabled={!!!txId} onClick={tweet}>Tweet about it</Button></HFlex>
    <HFlex sx={{ mt: 2 }}><Button variant="outlined" disabled={!!!txId} onClick={viewInNFTExplorer}>View NFT on Pera Explorer</Button></HFlex>
    <HFlex sx={{ mt: 2 }}><Button variant="outlined" disabled={!!!txId} onClick={viewInExplorer}>View transaction on Allo</Button></HFlex>
    <D />
    <HFlex>
      <ConnectButton account={account} onDisconnect={api.disconnectWallet}/>
    </HFlex>
  </Box>;
}

export default observer(NFT);
